import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getSession, cancelSession } from '../lib/api';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Loader2, ShieldCheck, CheckCircle2, XCircle, Clock,
  CalendarDays, MapPin, ArrowRight,
} from 'lucide-react';
import { CATEGORY_NAMES, formatCurrency, getInitials } from '../lib/utils';
import { toast } from 'sonner';

const POLL_MS = 3000;

const Countdown = ({ to }) => {
  const compute = () => Math.max(0, Math.floor((new Date(to).getTime() - Date.now()) / 1000));
  const [left, setLeft] = useState(compute);
  useEffect(() => {
    if (!to) return;
    const id = setInterval(() => setLeft(compute()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to]);
  if (!to) return null;
  const m = Math.floor(left / 60);
  const s = left % 60;
  return <span className="tabular-nums font-bold">{m}:{s.toString().padStart(2, '0')}</span>;
};

const SessionWaiting = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  const sid = sessionId || location.state?.sessionId;

  const refresh = useCallback(async () => {
    if (!sid) return;
    try {
      const res = await getSession(sid);
      setSession(res.data);
    } catch (e) {
      // 404 or 403, bounce to sessions list
      navigate('/sessions');
    } finally {
      setLoading(false);
    }
  }, [sid, navigate]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const status = session?.status;

  const handleCancel = async () => {
    if (!sid) return;
    setCancelling(true);
    try {
      await cancelSession(sid, 'client_cancelled');
      toast.message('Request cancelled, your card was not charged');
      navigate('/sessions');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel');
      setCancelling(false);
    }
  };

  const trainer = session?.trainer || {};
  const profile = trainer.trainer_profile || {};

  const heroIcon = useMemo(() => {
    if (status === 'confirmed') return <CheckCircle2 className="w-10 h-10 text-emerald-400" />;
    if (status === 'declined' || status === 'cancelled') return <XCircle className="w-10 h-10 text-red-400" />;
    return <Loader2 className="w-10 h-10 text-[#C0A062] animate-spin" />;
  }, [status]);

  const heroTitle = useMemo(() => {
    if (status === 'confirmed') return 'Confirmed!';
    if (status === 'declined') return session?.decline_reason === 'auto_expired'
      ? 'Trainer didn\u2019t respond in time'
      : 'Trainer declined this request';
    if (status === 'cancelled') return 'Request cancelled';
    return 'Waiting for trainer to confirm\u2026';
  }, [status, session?.decline_reason]);

  const heroBody = useMemo(() => {
    if (!session) return '';
    if (status === 'confirmed') {
      const isAtTrainer = session.session_location_type === 'trainer_address';
      return isAtTrainer
        ? `See you at ${session.location_address}.`
        : `${trainer.full_name} is on the way to ${session.location_address}.`;
    }
    if (status === 'declined') return 'Your card was not charged. Try another trainer or schedule for later.';
    if (status === 'cancelled') return 'Your card was not charged.';
    return 'Your card has been authorized, you\u2019ll only be charged if they accept within 5 minutes.';
  }, [session, status, trainer.full_name]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090B]">
        <div className="spinner" />
      </div>
    );
  }
  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090B]">
        <Button onClick={() => navigate('/sessions')}>Back to sessions</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] py-12 px-4" data-testid="session-waiting-page">
      <div className="max-w-md mx-auto">
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center">
          <div className="flex justify-center mb-4">{heroIcon}</div>
          <h1 className="text-2xl font-bold text-white mb-2" data-testid="waiting-status-title">{heroTitle}</h1>
          <p className="text-zinc-400 text-sm mb-6">{heroBody}</p>

          {status === 'pending_trainer_response' && session.auto_decline_at && (
            <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C0A062]/10 border border-[#C0A062]/30 text-[#C0A062]">
              <Clock className="w-4 h-4" />
              Auto-expires in <Countdown to={session.auto_decline_at} />
            </div>
          )}

          <div className="bg-[#09090B] border border-zinc-800 rounded-lg p-4 text-left space-y-3 mb-6">
            <div className="flex items-center gap-3">
              <Avatar className="w-10 h-10 border border-zinc-700">
                <AvatarImage src={profile.profile_image} />
                <AvatarFallback className="bg-zinc-800 text-[#C0A062]">{getInitials(trainer.full_name || 'T')}</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-white font-semibold">{trainer.full_name}</p>
                <p className="text-xs text-zinc-500">{CATEGORY_NAMES[session.category] || session.category}</p>
              </div>
            </div>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2 text-zinc-300"><CalendarDays className="w-3.5 h-3.5 text-[#C0A062]" />{session.scheduled_date} · {session.scheduled_time}</div>
              <div className="flex items-start gap-2 text-zinc-300"><MapPin className="w-3.5 h-3.5 text-[#C0A062] mt-0.5 shrink-0" /><span className="break-words">{session.location_address}</span></div>
              <div className="flex items-center gap-2 text-zinc-300"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />Total {formatCurrency(session.price)}</div>
            </div>
          </div>

          {status === 'pending_trainer_response' ? (
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={cancelling}
              className="w-full border-zinc-700 text-zinc-200 hover:bg-zinc-800"
              data-testid="waiting-cancel-btn"
            >
              {cancelling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Cancel request
            </Button>
          ) : (
            <Button
              onClick={() => navigate('/sessions')}
              className="w-full bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold"
              data-testid="waiting-to-sessions-btn"
            >
              Go to My Sessions
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionWaiting;
